import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getClassName } from "@utils";
import { useClickOutside } from "@taskpane/hooks";
import { getDropdownPositionStyles } from "./utils";
import css from "./Dropdown.module.scss";

export interface DropdownProps {
  isOpened: boolean;
  onClose: () => void;
  children?: React.ReactNode;
  className?: string;
  positionOffset?: number;
  width?: number | string;
  renderContent?: (props: { onClose: () => void }) => React.ReactNode;
}

const Dropdown: React.FC<DropdownProps> = ({
  isOpened: isOpenedProp,
  onClose,
  children,
  className,
  positionOffset = 8,
  width,
  renderContent,
}) => {
  const [isOpened, setIsOpened] = useState(isOpenedProp);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const attrs = {
    container: {
      className: css.container,
      ref: containerRef,
    },
    dropdown: {
      className: getClassName(css.dropdown, !isOpenedProp && css.dropdownClosing, className),
      ref: dropdownRef,
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
      },
      onAnimationEnd: () => {
        if (!isOpenedProp) {
          setIsOpened(false);
        }
      },
    },
  };

  const handleDropdownPosition = useCallback(() => {
    const dropdown = dropdownRef.current;
    const container = containerRef.current;

    if (isOpened && dropdown && container) {
      const transformStyle = getDropdownPositionStyles(dropdown, container, positionOffset);

      dropdown.style.transform = transformStyle;
    }
  }, [isOpened, positionOffset]);

  const handleDropdownWidth = useCallback(() => {
    const dropdown = dropdownRef.current;
    const container = containerRef.current;

    if (isOpened && dropdown && container) {
      dropdown.style.width =
        typeof width === "number" || typeof width === "string"
          ? typeof width === "number"
            ? `${width}px`
            : width
          : `${container.offsetWidth}px`;
    }
  }, [isOpened, width]);

  useEffect(() => {
    if (isOpenedProp) {
      setIsOpened(true);
    }
  }, [isOpenedProp]);

  useEffect(() => {
    handleDropdownWidth();
  }, [handleDropdownWidth]);

  useEffect(() => {
    handleDropdownPosition();
  }, [handleDropdownPosition]);

  useEffect(() => {
    const handler = () => {
      window.requestAnimationFrame(() => {
        handleDropdownPosition();
      });
    };

    window.addEventListener("resize", handler);
    document.addEventListener("scroll", handler, true);

    return () => {
      window.removeEventListener("resize", handler);
      document.removeEventListener("scroll", handler, true);
    };
  }, [handleDropdownPosition, isOpenedProp]);

  useClickOutside(dropdownRef, () => {
    window.requestAnimationFrame(() => {
      if (isOpenedProp) {
        onClose();
      }
    });
  });

  const renderDropdown = () => {
    if (!isOpened) {
      return null;
    }

    return createPortal(
      <div {...attrs.dropdown}>{renderContent ? renderContent({ onClose }) : null}</div>,
      document.body
    );
  };

  return (
    <div {...attrs.container}>
      {children}
      {renderDropdown()}
    </div>
  );
};

export default Dropdown;
